import {Component, Inject, Input, OnInit} from '@angular/core';
import {EmployeeEditHistoryEntry, EmployeeEditHistoryResource} from 'resteasytox';
import * as _ from 'lodash';
import * as moment from 'moment';
import {Duration, Moment} from 'moment';
import {EmployeeType} from '../settings-employees/settings-employees.component';
import {EmployeesEditHistoryCacheService} from '../../core/employee-edit-history-cache.service';
import {VIEW_DATE_FORMAT} from '../../reports/timecards-builder.service';
import {
	GLOBAL_NOTIFICATION_TYPE,
	GlobalNotificationsService
} from '../../core/components/global-notifications/global-notifications.service';

export interface EditableEmployeeEditHistoryEntry extends EmployeeEditHistoryEntry {
	editMode?: boolean;
	workStartTimeFormatted: string;
	workEndTimeFormatted: string;
	oldEntry?: EmployeeEditHistoryEntry;
}
interface EmployeeEditHistoryResponse {
	success: boolean;
	message?: string;
	entry?: EmployeeEditHistoryEntry;
}

@Component({
	selector: 'tt-employee-edit-history',
	templateUrl: 'settings-employee-edit-history.component.html',
	styleUrls: ['settings-employee-edit-history.component.scss']
})
export class SettingsEmployeesHistoryComponent implements OnInit {
	_employeeId: number;
	get employeeId(): number {
		return this._employeeId;
	}
	@Input('employeeId')
	set employeeId(value: number) {
		this._employeeId = value;
		this.reloadEmployeeHistory();
	}

	history: EditableEmployeeEditHistoryEntry[] = [];
	showModalAddForm: boolean = false;
	showModalEditForm: boolean = false;
	editModalEntry: EditableEmployeeEditHistoryEntry;
	newEntry: EmployeeEditHistoryEntry = this.initNewEntry();
	public EMPLOYEE_TYPE: any = EmployeeType;

	constructor(
		@Inject('EmployeeEditHistoryResource') private employeeEditHistoryResource: EmployeeEditHistoryResource,
		private employeesEditHistoryCacheService: EmployeesEditHistoryCacheService,
		private gns: GlobalNotificationsService
	) {

	}

	ngOnInit(): void {

	}

	private reloadEmployeeHistory(): void {
		if (!this.employeeId) { return; }

		const employeeEditHistory: EmployeeEditHistoryEntry[] = this.employeesEditHistoryCacheService.getEmployeeEditHistory(this.employeeId);
		const history: any[] = _.orderBy(employeeEditHistory, ['startDate'], ['desc']);

		this.history = history.map((entry: EmployeeEditHistoryEntry) => {
			const startTimeDuration: Duration = moment.duration(entry.workStartTime, 'ms');
			const endTimeDuration: Duration = moment.duration(entry.workEndTime, 'ms');
			return _.assign(entry, {
				startDateFormatted: moment.utc(entry.startDate).format(VIEW_DATE_FORMAT),
				endDateFormatted: (entry.endDate) ? moment.utc(entry.endDate).format(VIEW_DATE_FORMAT) : undefined,
				workStartTimeFormatted: (entry.workStartTime) ? this.convert24TimeTo12(startTimeDuration.hours() + ':' + startTimeDuration.minutes()) : undefined,
				workEndTimeFormatted: (entry.workEndTime) ? this.convert24TimeTo12(endTimeDuration.hours() + ':' + endTimeDuration.minutes()) : undefined
			});
		});
	}

	public enableEditMode(entry: EditableEmployeeEditHistoryEntry): void {
		// entry.oldEntry = _.assign({}, entry);
		// entry.editMode = true;
		this.editModalEntry = entry;
		this.showModalEditForm = true;
	}
	public cancelEdit(): void {
		this.showModalEditForm = false;
		this.editModalEntry = null;
		// entry.editMode = false;
		// this.restoreOldEntry(entry);
	}
	public saveEntry(entry: EditableEmployeeEditHistoryEntry): void {
		if (entry.workStartTimeFormatted) {
			if (!this.isValid12HoursTime(entry.workStartTimeFormatted)) {
				alert('Work Start Time is not valid');
				return;
			}
		}
		if (entry.workEndTimeFormatted) {
			if (!this.isValid12HoursTime(entry.workEndTimeFormatted)) {
				alert('Work End Time is not valid');
				return;
			}
		}

		const data: EmployeeEditHistoryEntry = _.assign(entry, {
			employeeId: this.employeeId,
			startDate: entry.startDateFormatted,
			endDate: entry.endDateFormatted,
			workStartTime: (!entry.workStartTimeFormatted) ? undefined : moment.duration(this.convert12TimeTo24(entry.workStartTimeFormatted)).asMilliseconds(),
			workEndTime: (!entry.workEndTimeFormatted) ? undefined : moment.duration(this.convert12TimeTo24(entry.workEndTimeFormatted)).asMilliseconds()
		});
		const updatedEntry: EmployeeEditHistoryEntry = new EmployeeEditHistoryEntry().initFromData(data);

		this.employeeEditHistoryResource.put(updatedEntry)
			.then((res: EmployeeEditHistoryResponse) => {
				this.employeesEditHistoryCacheService.updateData()
					.subscribe(() => {
						if (res.success) {
							this.reloadEmployeeHistory();
							this.cancelAddForm();
						} else {
							alert(res.message);
						}
					});
			})
			.catch((err: any) => {
				console.error(err);
				alert(err);
			});
	}
	public deleteEntry(entry: EditableEmployeeEditHistoryEntry): void {
		if (!confirm('Are you sure you want to delete this record?')) { return; }
		this.employeeEditHistoryResource.delete(entry.id)
			.then(() => {
				this.employeesEditHistoryCacheService.updateData()
					.subscribe(() => {
						this.reloadEmployeeHistory();
					});
			})
			.catch((err: any) => {
				alert(err);
				console.error(err);
			});
	}

	public enableAddForm(): void {
		this.showModalAddForm = true;
	}
	public disableAddForm(): void {
		this.showModalAddForm = false;
	}
	public cancelAddForm(): void {
		this.newEntry = this.initNewEntry();
		this.disableAddForm();
	}
	public onAddModalClose(): void {
		this.cancelAddForm();
	}

	public onNewEntryAdded(): void {
		this.gns.sendNotification(GLOBAL_NOTIFICATION_TYPE.SUCCESS, 'New entry added', 5000);
		this.reloadEmployeeHistory();
	}

	public onEditModalClose(): void {
		this.reloadEmployeeHistory();
		this.cancelEdit();
	}

	public onEntryEdited(entry: EditableEmployeeEditHistoryEntry): void {
		this.gns.sendNotification(GLOBAL_NOTIFICATION_TYPE.SUCCESS, 'Entry updated', 5000);
		this.reloadEmployeeHistory();
		this.cancelEdit();
	}

	public formatEmployeeTypeToReadable(value: EmployeeType): string {
		if (value === null || value === undefined) { return; }
		value = parseInt(value.toString(), 10);
		if (value === EmployeeType.DAILY_1099) {
			return '1099 Daily';
		} else if (value === EmployeeType.HOURLY_1099) {
			return '1099 Hourly';
		} else if (value === EmployeeType.DAILY_W2) {
			return 'W-2 Daily';
		} else if (value === EmployeeType.HOURLY_W2) {
			return 'W-2 Hourly';
		}
	}

	private initNewEntry(): EmployeeEditHistoryEntry  {
		const newEntry: EmployeeEditHistoryEntry = new EmployeeEditHistoryEntry();

		newEntry['workStartTimeFormatted'] = '8:30 am';
		newEntry['workEndTimeFormatted'] = '5:30 pm';

		return newEntry;
	}

	private restoreOldEntry(entry: EditableEmployeeEditHistoryEntry ): void {
		const oldEntry: EmployeeEditHistoryEntry = new EmployeeEditHistoryEntry().initFromData(entry.oldEntry);
		const oldValues: any = _.omit(entry.oldEntry, 'oldEntry');
		delete entry.oldEntry;
		_.forIn(oldValues, (value: any, key: string) => {
			entry[key] = value;
		});
	}

	private isValid12HoursTime(time: string): boolean {
		const blankRegex: RegExp =  /\s+/gmi;
		const isValidTimeRegex: RegExp = /[0-9]{1,2}:[0-9]{1,2}(am|pm)?$/gmi;
		time = time.replace(blankRegex, '').toLowerCase();
		return isValidTimeRegex.test(time);
	}

	private convert12TimeTo24(time: string): string {
		const blankRegex: RegExp =  /\s+/gmi;
		time = time.replace(blankRegex, '').toLowerCase();

		const postfix: string = (time.search('pm') !== -1) ? 'pm' : 'am';
		time = time.replace('am', '').replace('pm', '');
		if (postfix === 'am') {
			return time;
		} else {
			const timeParts: string[] = time.split(':');
			return (parseInt(timeParts[0], 10) + 12) % 24 + ':' + timeParts[1];
		}
	}

	private convert24TimeTo12(time24: string): string {
		const blankRegex: RegExp =  /\s+/gmi;
		time24 = time24.replace(blankRegex, '').toLowerCase();
		const timeParts: string[] = time24.split(':');
		const hours: number = parseInt(timeParts[0], 10);
		let amHours: number = hours;
		let postfix: string = 'am';
		if (hours > 12) {
			postfix = 'pm';
			amHours = hours % 12;
		}



		return ((amHours < 10) ? '0' + amHours : amHours) + ':' + ( (timeParts[1].length < 2) ? '0' + timeParts[1] : timeParts[1]) + postfix;
	}

	private formatEntryDate(date: Date): string {
		return moment(date).format('MM/DD/YYYY');
	}

	private formatEntryWorkingTime(date: Date, entry: EditableEmployeeEditHistoryEntry, fieldName: string): void {
		entry[fieldName] = moment.duration(moment(date).format('HH:MM'));
	}


}



// WEBPACK FOOTER //
// ./src/app/settings/settings-employee-edit-history/settings-employee-edit-history.component.ts